import { useState, useEffect } from "react";
import { X, MapPin, Copy } from "lucide-react";
import { createPortal } from "react-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import { geocodeAddress } from "../utils/geocoding";
import { useTheme } from "../context/ThemeContext";

// Fix for Leaflet marker icons
const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

interface StoreMapModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeAddress: string;
  storeName?: string;
  coordinates?: [number, number]; // Optional coordinates [lat, lng]
}

export default function StoreMapModal({
  isOpen,
  onClose,
  storeAddress,
  storeName,
  coordinates,
}: StoreMapModalProps) {
  const { isDark } = useTheme();
  const [isAnimating, setIsAnimating] = useState(false);
  const [position, setPosition] = useState<[number, number] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setIsAnimating(true), 10);

      // If coordinates are provided, use them directly
      if (
        coordinates &&
        coordinates.length === 2 &&
        !isNaN(coordinates[0]) &&
        !isNaN(coordinates[1])
      ) {
        setPosition(coordinates);
        setLoading(false);
      } else {
        // Otherwise, geocode the full address to get coordinates
        setLoading(true);

        // Use geocoding utility with full address
        geocodeAddress(storeAddress)
          .then((coords) => {
            if (coords && !isNaN(coords.lat) && !isNaN(coords.long)) {
              setPosition([coords.lat, coords.long]);
            } else {
              // Fallback coordinates (center of US) if geocoding fails
              console.error("Failed to geocode address:", storeAddress);
              setPosition([39.8283, -98.5795]);
            }
          })
          .catch((err) => {
            console.error("Geocoding error:", err);
            setPosition([39.8283, -98.5795]);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    } else {
      setIsAnimating(false);
      setPosition(null);
    }
  }, [isOpen, storeAddress, coordinates]);

  if (!isOpen) return null;

  // Use coordinates if available, otherwise fall back to address
  const googleMapsLink = position
    ? `https://www.google.com/maps/search/?api=1&query=${position[0]},${position[1]}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        storeAddress
      )}`;

  // Map tile URLs - dark mode uses CartoDB dark tiles
  const tileUrl = isDark
    ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
    : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
  const tileAttribution = isDark
    ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
    : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
      style={{
        backgroundColor: isAnimating ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0)",
        backdropFilter: isAnimating ? "blur(4px)" : "blur(0px)",
        transition: "background-color 250ms ease-out, backdrop-filter 250ms ease-out",
      }}
      onClick={onClose}
    >
      <div
        className={`bg-white dark:bg-dark-800 w-full sm:w-auto sm:min-w-[500px] sm:max-w-2xl rounded-t-xl sm:rounded-xl overflow-hidden flex flex-col shadow-2xl transform transition-all duration-300 ease-out ${
          isAnimating
            ? "translate-y-0 sm:scale-100 opacity-100"
            : "translate-y-full sm:translate-y-0 sm:scale-95 sm:opacity-0"
        }`}
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-dark-700 bg-white dark:bg-dark-800">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-primary-50 dark:bg-primary-900/40 rounded-full">
              <MapPin className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {storeName || "Store Location"}
              </h3>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(storeAddress);
                  const el = document.getElementById("store-address-copied");
                  if (el) { el.classList.remove("opacity-0"); setTimeout(() => el.classList.add("opacity-0"), 1500); }
                }}
                className="text-xs text-gray-600 dark:text-dark-200 sm:max-w-md text-left hover:text-purple-600 dark:hover:text-purple-300 transition-colors flex items-start gap-1 group"
              >
                <span>{storeAddress}</span>
                <Copy className="w-3 h-3 flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
              <span id="store-address-copied" className="text-[9px] text-green-600 dark:text-green-400 opacity-0 transition-opacity">Copied!</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 dark:text-dark-400 hover:text-gray-600 dark:hover:text-dark-200 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Map Container */}
        <div className="relative w-full h-[400px] bg-gray-100 dark:bg-dark-900">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-dark-900">
              <div className="flex flex-col items-center space-y-2">
                <div className="w-8 h-8 border-4 border-primary-200 dark:border-primary-800 border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin"></div>
                <span className="text-sm text-gray-500 dark:text-dark-400">Loading map...</span>
              </div>
            </div>
          ) : position ? (
            <MapContainer
              center={position}
              zoom={13}
              style={{ width: "100%", height: "100%" }}
            >
              <TileLayer
                attribution={tileAttribution}
                url={tileUrl}
              />
              <Marker position={position}>
                <Popup>
                  <div className="text-sm">
                    <strong>{storeName}</strong>
                    <br />
                    {storeAddress}
                  </div>
                </Popup>
              </Marker>
            </MapContainer>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-dark-900 text-gray-500 dark:text-dark-400">
              Map unavailable
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-100 dark:border-dark-700 bg-gray-50 dark:bg-dark-900">
          <a
            href={googleMapsLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center w-full py-2.5 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors shadow-sm"
          >
            <MapPin className="w-4 h-4 mr-2" />
            Open in Google Maps
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
}
